
import parse from 'html-react-parser';
import moment from 'moment';

import { FormattedMessage, useIntl } from 'react-intl';

import NoHotelResults from '@assets/images/empty/hotels-empty.jpg';
import NoCarsResults from '@assets/images/empty/cars-empty.jpg';

import { getBudgetLabelText, getFilterValues } from '@utils';
import { useSelector } from 'react-redux';
import { HomesTypes } from '@share/constants';
import { getSelectedCurrency, RootState } from '@share/utils';
import { IAccount } from '@share/common-types';

import './style.scss';

interface IProps {
  isCars?: boolean;
}

const LABELS = {
  refundable: 'refundability',
  amenities: 'tab.amenities',
  starRatings: 'star.rating',
  accommodationType: 'accommodation.type',
  accessibility: 'accessibility',
  popularLocations: 'popular.locations',
  budget: 'budget',
  boardNames: 'meal.plan',

  airCondition: 'cars.filter.air_condition',
  carType: 'cars.filter.car_type',
  simpleType: 'cars.filter.car_type.detailed',
  category: 'cars.filter.category',
  doors: 'cars.filter.doors',
  driveType: 'cars.filter.drive_type',
  fuel: 'cars.filter.fuel',
  rateDistance: 'cars.filter.rate_distance',
  rentalCompany: 'cars.filter.rental_company',
  seats: 'cars.filter.seats',
  transmissionType: 'cars.filter.transmission_type',
  whereLocation: 'where.location',
}

export function EmptyFilterMessage(props: IProps) {
  const { isCars } = props;

  const loginStore = useSelector((state: RootState) => state.loginStore);
  
  const filtersStore = useSelector((state: RootState) => state.filtersStore);
  const locationsStore = useSelector((state: RootState) => state.locationsStore);
  const roomsStore = useSelector((state: RootState) => state.roomsStore);
  const datesStore = useSelector((state: RootState) => state.datesStore);

  const carsFiltersStore = useSelector((state: RootState) => state.carsFiltersStore);
  const carsLocationsStore = useSelector((state: RootState) => state.carsLocationsStore);
  const carsDatesStore = useSelector((state: RootState) => state.carsDatesStore);
  
  const { locations, locationLabel } = locationsStore;
  const { pickUp, dropOff } = carsLocationsStore;
  const { rooms } = roomsStore;
  const { startDate, endDate } = datesStore;
  const { startDate: carsStartDate, startDateTime: carsStartDateTime, endDate: carsEndDate, endDateTime: carsEndDateTime } = carsDatesStore;
  const { searchHomes } = filtersStore;
  const { account } = loginStore;
  
  const label = isCars ? 'no.showing.filter.cars' : 'no.showing.filter';
  const msg = "no.showing.filter.description";

  const image = isCars ? NoCarsResults : NoHotelResults;
  
  const currentFilters: string[] = [];

  const intl = useIntl();

  const getRoomMessage = (room: any) => {
    return `adults: ${room?.adultsCount}${room?.kids?.length ? `, kids: ${room?.kids?.length}` : ''}`;
  }

  const getRoomsMessage = (rooms: any[]) => {
    if (rooms.length === 1) {
      return `Rooms: ${getRoomMessage(rooms?.[0])}`;
    }

    const currentFilters: string[] = ['Rooms: <br />'];
    rooms.forEach((room: any, index: number) => {
      currentFilters.push(`${Array(10).fill('&nbsp;').join('')}Room ${index + 1}: ${getRoomMessage(room)}<br />`);
    });

    return currentFilters.join('');
  }

  const getDateMessage = (date: string, time?: string) => {
    return `${date ? moment(date, 'yyyy-MM-dd').format('DD MMM YYYY') : ''}${time ? ` ${time}` : ''}`;
  }

  const filterValues = getFilterValues(isCars ? carsFiltersStore : filtersStore, undefined, isCars);

  if (isCars) {
    currentFilters.push(`${intl.formatMessage({ id: 'cars.pickup' })}: ${pickUp.locationLabel || pickUp.name}`);
    if(dropOff.name !== pickUp.name) {
      currentFilters.push(`${intl.formatMessage({ id: 'cars.dropoff' })}: ${dropOff.locationLabel || dropOff.name}`);
    }
    currentFilters.push(`${intl.formatMessage({ id: 'cars.dates' })}: ${getDateMessage(carsStartDate, carsStartDateTime)} - ${getDateMessage(carsEndDate, carsEndDateTime)}`);
  } else {
    currentFilters.push(`${intl.formatMessage({ id: 'location' })}: ${locationLabel || locations[0].name}`);
    currentFilters.push(`${intl.formatMessage({ id: 'dates' })}: ${getDateMessage(startDate)} - ${getDateMessage(endDate)}`);
    currentFilters.push(getRoomsMessage(rooms));


    if (searchHomes !== HomesTypes.All) {
      currentFilters.push(`Home type: ${intl.formatMessage({ id: searchHomes === HomesTypes.VacationRentalOnly ? 'houses' : 'hotels' })}`);
    }
  }

  if(filterValues.length > 0) {
    // Group filterValues by key
    const groupedFilters = filterValues.reduce((acc: any, filter: any) => {
      if (!acc[filter.key]) {
        acc[filter.key] = [];
      }
      acc[filter.key].push(filter.value);
      return acc;
    }, {});

    // Display grouped filters
    Object.entries(groupedFilters).forEach(([key, values]: [string, any]) => {
      const valuesString = Array.isArray(values) ? values.join(', ') : values;
      const label = LABELS[key as keyof typeof LABELS] || key;

      const vs = key === 'budget' ? 
                    getBudgetLabelText(valuesString, getSelectedCurrency(account as IAccount), intl) : 
                    key === 'starRatings' ? 
                        intl.formatMessage({ id: "stars.count" }, { count: valuesString }) : 
                        valuesString

      currentFilters.push(`${intl.formatMessage({ id: label })}: ${vs}`);
    });
  }
  
  return (
    <div className="no-filter-msg">
      <h1 id="no-results-title" className="title"><FormattedMessage id={label} /></h1>

      <main className="card-filters" role="main" aria-labelledby="no-results-title">
        <section>
          <p className="desc"><FormattedMessage id={msg} /></p>
          <ul className="suggestions">
            {currentFilters.map((filter) => (
              <li key={filter}>{parse(filter)}</li>
            ))}
          </ul>

          <h3 style={{ margin: '0 0 10px 0', fontSize: '16px'}}>Suggestions</h3>
          <ul className="suggestions">
            <li>Broaden your price range</li>
            <li>Try a different neighborhood or nearby city</li>
            <li>Remove or relax some filters (like breakfast or free cancellation)</li>
          </ul>
        </section>

        <aside className="illustration" aria-hidden="false">
          <img src={image} alt="" />
        </aside>
      </main>
    </div>
  );
}
